import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../../store';
import { selectElectricConfigs } from '../../../store/selectors/databaseSelectors';
import { addElectricConfig } from '../../../store/slices/databaseSlice';
import { electricConfigService } from '../../../services';
import { TOAST_CONFIG, TOAST_WARNING_CONFIG } from '../../../constants';
import toast from 'react-hot-toast';

interface ElectricConfigForm {
  type: string;
  value: string;
}

// Configuration categories with their display names
const CONFIG_CATEGORIES = [
  // { 
  //   id: 'electrical_modifications', 
  //   label: 'Electrical Modifications',
  //   description: 'Main breaker derate, meter adapter, etc.',
  //   color: 'yellow'
  // },
  { 
    id: 'main_breaker_size', 
    label: 'Main Breaker Size',
    description: 'Amperage ratings for main breakers',
    color: 'orange'
  },
  { 
    id: 'subpanel_size', 
    label: 'Subpanel Size',
    description: 'Amperage ratings for subpanels',
    color: 'red'
  },
  { 
    id: 'feeding_method', 
    label: 'Feeding Method',
    description: 'How the subpanel is fed',
    color: 'purple'
  },
  { 
    id: 'ocpd', 
    label: 'OCPD Size',
    description: 'Overcurrent protection device sizes',
    color: 'blue'
  },
  { 
    id: 'pv_breaker_size', 
    label: 'PV Breaker Size',
    description: 'PV breaker amperage ratings',
    color: 'green'
  },
  { 
    id: 'pv_breaker_location', 
    label: 'PV Breaker Location',
    description: 'Where the PV breaker is located',
    color: 'indigo'
  }
];

// Color mapping for tags
const getColorClasses = (color: string) => {
  const colorMap: Record<string, { bg: string; text: string; border: string }> = {
    yellow: { bg: 'bg-yellow-50', text: 'text-yellow-800', border: 'border-yellow-200' },
    orange: { bg: 'bg-orange-50', text: 'text-orange-800', border: 'border-orange-200' },
    red: { bg: 'bg-red-50', text: 'text-red-800', border: 'border-red-200' },
    purple: { bg: 'bg-purple-50', text: 'text-purple-800', border: 'border-purple-200' },
    blue: { bg: 'bg-blue-50', text: 'text-blue-800', border: 'border-blue-200' },
    green: { bg: 'bg-green-50', text: 'text-green-800', border: 'border-green-200' },
    indigo: { bg: 'bg-indigo-50', text: 'text-indigo-800', border: 'border-indigo-200' }
  };
  return colorMap[color] || colorMap.blue;
};

export const ElectricConfigManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  
  // Get data from Redux
  const electricConfigs = useAppSelector(selectElectricConfigs);
  
  // Form states
  const [configForm, setConfigForm] = useState<ElectricConfigForm>({
    type: '',
    value: ''
  });
  
  // UI states
  const [activeCategory, setActiveCategory] = useState<string>('main_breaker_size');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localConfigs, setLocalConfigs] = useState(electricConfigs);

  // Initialize local state with Redux data
  useEffect(() => {
    setLocalConfigs(electricConfigs);
  }, [electricConfigs]);

  // Get configurations by category
  const getConfigsByCategory = (category: string) => {
    return localConfigs
      .filter(config => config.type === category)
      .sort((a, b) => {
        // Sort numerically if possible
        const aNum = parseInt(a.value.replace(/[^0-9]/g, ''));
        const bNum = parseInt(b.value.replace(/[^0-9]/g, ''));
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return aNum - bNum;
        }
        return a.value.localeCompare(b.value);
      });
  };

  // Get placeholder based on category
  const getPlaceholder = (type: string) => {
    switch (type) {
      case 'electrical_modifications':
        return 'e.g., MAIN BREAKER DERATE, METER ADAPTER';
      case 'main_breaker_size':
        return 'e.g., 100A, 125A, 200A';
      case 'subpanel_size':
        return 'e.g., 100A, 125A, 200A';
      case 'feeding_method':
        return 'e.g., Breaker in MSP, Feed through lug kit';
      case 'ocpd':
        return 'e.g., 50A, 60A, 100A';
      case 'pv_breaker_size':
        return 'e.g., 20A, 30A, 40A';
      case 'pv_breaker_location':
        return 'e.g., MSP, SUB';
      default:
        return 'Enter value';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!activeCategory || !configForm.value.trim()) {
      toast('Please enter a value', TOAST_WARNING_CONFIG);
      return;
    }

    // Process the value based on category type
    let processedValue = configForm.value.trim();
    
    // For all amperage categories, keep numeric value only (no "A" suffix)
    const amperageCategories = ['main_breaker_size', 'subpanel_size', 'ocpd', 'pv_breaker_size'];
    if (amperageCategories.includes(activeCategory)) {
      processedValue = processedValue.replace(/[^0-9]/g, '');
    } else {
      processedValue = processedValue.toUpperCase();
    }

    // Check if this value already exists for this category
    const exists = localConfigs.some(
      config => config.type === activeCategory && 
               config.value.toLowerCase() === processedValue.toLowerCase()
    );
    
    if (exists) {
      toast('This value already exists for the selected category', TOAST_WARNING_CONFIG);
      return;
    }

    setIsSubmitting(true);
    try {
      const newConfig = await electricConfigService.createElectricConfig({
        type: activeCategory,
        value: processedValue
      });
      
      // Update Redux state
      dispatch(addElectricConfig(newConfig));
      
      // Update local state
      setLocalConfigs(prev => [...prev, newConfig]);
      
      // Reset form
      setConfigForm({ type: '', value: '' });
      
      toast.success('Configuration added successfully!', TOAST_CONFIG.toastOptions.success);
    } catch (error) {
      console.error('Error creating electric configuration:', error);
      toast.error('Failed to create configuration. Please try again.', TOAST_CONFIG.toastOptions.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const activeConfig = CONFIG_CATEGORIES.find(cat => cat.id === activeCategory);
  const colorClasses = activeConfig ? getColorClasses(activeConfig.color) : getColorClasses('blue');

  return (
    <div className="p-4">
      {/* Category Tabs */}
      <div className="mb-6 border-b border-slate-200">
        <div className="flex flex-wrap gap-2 -mb-px">
          {CONFIG_CATEGORIES.map((category) => {
            const isActive = activeCategory === category.id;
            const count = getConfigsByCategory(category.id).length;
            return (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`
                  px-4 py-2.5 text-sm font-medium rounded-t-lg transition-all duration-200
                  ${isActive 
                    ? 'bg-white border-t border-x border-slate-200 text-slate-900 -mb-px' 
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }
                `}
              >
                <div className="flex items-center gap-2">
                  <span>{category.label}</span>
                  <span className={`
                    text-xs px-1.5 py-0.5 rounded-full font-normal
                    ${isActive 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'bg-slate-100 text-slate-600'
                    }
                  `}>
                    {count}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Form Section */}
        <div className="bg-white rounded-lg p-5 border border-slate-200 shadow-sm">
          <div className="mb-4">
            <h3 className="text-base font-semibold text-slate-900">
              Add New {activeConfig?.label}
            </h3>
            <p className="text-xs text-slate-600 mt-1">
              {activeConfig?.description}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                Value *
              </label>
              {['main_breaker_size', 'subpanel_size', 'ocpd', 'pv_breaker_size'].includes(activeCategory) ? (
                <div className="relative">
                  <input
                    type="number"
                    min="1"
                    value={configForm.value}
                    onChange={(e) => setConfigForm({ ...configForm, value: e.target.value })}
                    placeholder="e.g., 200"
                    className="w-full px-3 py-2 pr-8 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-slate-500">A</span>
                </div>
              ) : (
                <input
                  type="text"
                  value={configForm.value}
                  onChange={(e) => setConfigForm({ ...configForm, value: e.target.value })}
                  placeholder={getPlaceholder(activeCategory)}
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              )}
            </div>
            
            <button
              type="submit"
              disabled={isSubmitting || !configForm.value.trim()}
              className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isSubmitting ? 'Adding...' : `Add ${activeConfig?.label}`}
            </button>
          </form>
        </div>

        {/* List Section */}
        <div className="bg-white rounded-lg p-5 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-slate-900">
              Existing {activeConfig?.label} Values
            </h3>
            <span className="text-xs text-slate-500">
              {getConfigsByCategory(activeCategory).length} items
            </span>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {getConfigsByCategory(activeCategory).length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {getConfigsByCategory(activeCategory).map((config) => {
                  // Add "A" suffix for display in amperage categories
                  const amperageCategories = ['main_breaker_size', 'subpanel_size', 'ocpd', 'pv_breaker_size'];
                  const displayValue = amperageCategories.includes(activeCategory) ? `${config.value}A` : config.value;
                  
                  return (
                    <div 
                      key={config.id} 
                      className={`inline-flex items-center px-3 py-1.5 rounded-full border ${colorClasses.bg} ${colorClasses.text} ${colorClasses.border} hover:shadow-sm transition-shadow`}
                    >
                      <span className="text-sm font-medium">{displayValue}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-slate-400 mb-2">
                  <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <p className="text-slate-500 text-sm">
                  No {activeConfig?.label?.toLowerCase()} values configured yet
                </p>
                <p className="text-slate-400 text-xs mt-1">
                  Add your first value using the form
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};